import { ReactNode, useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Accordion, Button, CardNotification, Heading, Loading, Text } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { sessionAssignationStore, hasSessionAssignationChanges } from "@context/sessionAssignation";
import {
  canCreateOccurrence,
  canEditAssignation,
  duplicateOccurrencesCount,
  useSessionAssignationWorkflow,
  visibleOccurrences,
} from "@services/collections/session-assignation";
import { AppContext } from "../../context/EditSessionsContext";
import { useSessionAssignationEditing } from "../../hooks/session-assignation/useSessionAssignationEditing";
import { useSessionAssignationPolicy } from "../../hooks/session-assignation/useSessionAssignationPolicy";
import { useSessionAssignationInitialization } from "../../hooks/session-assignation";
import {
  canEditOccurrence,
  rowBlocksCount,
  sectionComponentName,
} from "../../utils/session-assignation";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Modal from "@components/modal";
import SessionAssignationToolbar from "../../components/session-assignation-toolbar";
import SessionAssignationTable from "../../components/session-assignation-table";
import SessionAssignationEditor from "../../components/session-assignation-editor";
import SessionResourceDrawer from "../../components/session-resource-drawer";
import SessionAssignationResults from "../session-assignation-results";
import styles from "./sessionAssignationView.module.scss";

interface SessionAssignationViewProps {
  heightHeader: number;
  viewSwitch: ReactNode;
  isAssignationLocked: boolean;
}

const SessionAssignationView = ({
  heightHeader,
  viewSwitch,
  isAssignationLocked,
}: SessionAssignationViewProps) => {
  const { id: linkId, scenario: scenarioId, origin: originId }: IParams = useParams();
  const { state } = useContext(AppContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { editorUrl } = useNavigationUrl();
  const load = useSessionAssignationInitialization({ scenarioId, originId, linkId });
  const workflow = useSessionAssignationWorkflow();
  useSessionAssignationPolicy(isAssignationLocked);
  const courseId = state.link?.info?.course?.id;
  const editing = useSessionAssignationEditing(courseId);
  const { draft } = editing;
  const [showConsolidation, setShowConsolidation] = useState(false);

  const reload = async () => {
    sessionAssignationStore.getState().backToEditing();
    return load();
  };
  const validate = async (acknowledgeConsolidation = false) => {
    setShowConsolidation(false);
    const outcome = await workflow.validate({ acknowledgeConsolidation });
    if (outcome === "consolidation-warning") setShowConsolidation(true);
  };
  const busy = draft.phase === "validating";
  const canEdit = canEditAssignation(draft.policy) && draft.phase === "editing";
  const canCreate =
    canCreateOccurrence(draft.policy) && draft.phase === "editing" && !editing.creation;
  const visible = visibleOccurrences(draft.rows, draft.filters);
  const selectedBlocks = editing.selected.reduce((count, row) => count + rowBlocksCount(row), 0);
  const modifiedBlocks = draft.rows
    .filter(row => row.pendingState !== "unchanged")
    .reduce((count, row) => count + rowBlocksCount(row), 0);
  if (["results", "committing", "saved"].includes(draft.phase))
    return <SessionAssignationResults heightHeader={heightHeader} onReload={reload} />;
  const editorCanEdit =
    !!editing.editor &&
    (editing.editor.kind === "create"
      ? canCreateOccurrence(draft.policy)
      : canEditOccurrence(editing.editor.values, draft));
  const resourceRow = draft.rows.find(row => row.localId === editing.resource?.rowId);
  const resourceDisabled =
    !!editing.resource &&
    (!draft.policy.abilities[`can_assign_${editing.resource.field}`] ||
      (!!resourceRow && !canEditOccurrence(resourceRow, draft)) ||
      !canEdit);

  return (
    <>
      <SessionAssignationToolbar
        viewSwitch={viewSwitch}
        selectedCount={editing.selected.length}
        canDelete={canEdit && editing.canDelete && !draft.editorDirty}
        canRestore={canEdit && editing.canRestore && !draft.editorDirty}
        canDuplicate={canEdit && editing.canDuplicate && !draft.editorDirty}
        canValidate={canEdit && hasSessionAssignationChanges(draft) && !draft.editorDirty}
        busy={busy}
        onDelete={editing.deleteSelected}
        onRestore={editing.restoreSelected}
        onDuplicate={editing.duplicateSelected}
        onValidate={() => validate()}
        onGoToLeague={() =>
          history.push(editorUrl("link", linkId), { currentSelectedWeek: state.link.selectedWeek })
        }
      />
      <section className={styles.content}>
        <div className={styles.heading}>
          <Heading type="h2">{t("assignation-edit.session-view.title")}</Heading>
          <div className={styles.counters}>
            <Text type="sm">
              {t("assignation-edit.session-view.selected-blocks")} <strong>{selectedBlocks}</strong>
            </Text>
            <Text type="sm">
              {t("assignation-edit.session-view.modified-blocks")} <strong>{modifiedBlocks}</strong>
            </Text>
          </div>
        </div>
        {["idle", "loading"].includes(draft.phase) && <Loading />}
        {draft.phase === "load-error" && (
          <CardNotification
            state="error"
            title={t("assignation-edit.session-view.errors.LOAD_FAILED")}
          >
            <Button variant="ghost" onClick={load}>
              {t("assignation-edit.session-view.retry")}
            </Button>
          </CardNotification>
        )}
        {(draft.issues.length > 0 || editing.issue) && (
          <div role="alert">
            <CardNotification
              state="error"
              title={t("assignation-edit.session-view.validation-errors")}
            >
              {[...draft.issues, ...(editing.issue ? [{ code: editing.issue }] : [])].map(
                (issue, index) => (
                  <Text key={index}>
                    {issue.message ??
                      t(`assignation-edit.session-view.errors.${issue.code}`, {
                        defaultValue: t("assignation-edit.session-view.errors.VALIDATION_FAILED"),
                      })}
                  </Text>
                ),
              )}
            </CardNotification>
          </div>
        )}
        {draft.phase === "editing" && !canEditAssignation(draft.policy) && (
          <CardNotification state="info" title={t("assignation-edit.session-view.read-only")} />
        )}
        {["editing", "validating"].includes(draft.phase) &&
          draft.sections.map((section, index) => {
            const rows = visible.filter(row => row.sectionId === section.id);
            const count = draft.rows.filter(
              row => row.sectionId === section.id && row.pendingState !== "deleted",
            ).length;
            const creation = editing.creation?.sectionId === section.id ? editing.creation : null;
            return (
              <Accordion
                key={section.id}
                className={styles.component}
                title={t("assignation-edit.session-view.component-title", {
                  component: sectionComponentName(section),
                  count,
                })}
                opened={index === 0 || !!creation}
                actionContent={
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon="circle-plus"
                    disabled={!canCreate}
                    onClick={event => {
                      event.stopPropagation();
                      editing.create(section.id);
                    }}
                  >
                    {t("assignation-edit.session-view.create")}
                  </Button>
                }
              >
                <SessionAssignationTable
                  draft={draft}
                  rows={rows}
                  selectedIds={editing.selectedIds}
                  creation={creation}
                  canEditInstructors={!!courseId}
                  onSelect={editing.toggleSelection}
                  onDate={(rowId, date) => editing.patchRow(rowId, { date })}
                  onCreationDate={editing.changeCreationDate}
                  onTime={row => (row ? editing.editTime(row) : editing.editCreationTime())}
                  onResources={editing.openResources}
                  onUndoDeletion={editing.undoDeletion}
                  onCancelCreation={editing.cancelCreation}
                />
              </Accordion>
            );
          })}
        {draft.phase === "editing" && !draft.sections.length && (
          <CardNotification state="info" title={t("assignation-edit.session-view.no-components")} />
        )}
      </section>
      <SessionAssignationEditor
        isOpen={editing.editorOpen}
        target={editing.editor}
        blocks={draft.blocks}
        canChangeTime={editorCanEdit && !!draft.policy.abilities.can_assign_blocks}
        onDirty={editing.setDrawerDirty}
        onApply={editing.applyEditor}
        onRequestClose={editing.closeDrawer}
        onClose={editing.finishEditorClose}
      />
      <SessionResourceDrawer
        isOpen={editing.resourceOpen && (editing.resource?.field === "classrooms" || !!courseId)}
        target={editing.resource}
        courseId={courseId}
        disabled={resourceDisabled}
        onDirty={editing.setDrawerDirty}
        onApply={editing.applyResources}
        onRequestClose={editing.closeDrawer}
        onClose={editing.finishResourceClose}
      />
      <Modal
        typeState="warning"
        show={showConsolidation}
        title={t("assignation-edit.session-view.consolidation.title")}
        textButtonPrincipal={t("assignation-edit.session-view.consolidation.confirm")}
        textButtonSecondary={t("generals.cancel")}
        colorButtonSecondary="ghost"
        onClose={() => setShowConsolidation(false)}
        onClickSecondary={() => setShowConsolidation(false)}
        onClickPrincipal={() => validate(true)}
      >
        <Text>
          {t("assignation-edit.session-view.consolidation.description", {
            count: duplicateOccurrencesCount(draft.rows),
          })}
        </Text>
      </Modal>
      <Modal
        typeState="error"
        show={!!editing.deletionIds}
        title={t("assignation-edit.session-view.delete-all.title", {
          component: editing.emptyingSections.map(sectionComponentName).join(", "),
        })}
        textButtonPrincipal={t("assignation-edit.session-view.delete-all.confirm")}
        colorButtonPrincipal="danger"
        textButtonSecondary={t("generals.cancel")}
        colorButtonSecondary="ghost"
        onClose={editing.cancelDeletion}
        onClickSecondary={editing.cancelDeletion}
        onClickPrincipal={editing.confirmDeletion}
      >
        <Text>
          {t("assignation-edit.session-view.delete-all.description", {
            count: editing.deletionIds?.length ?? 0,
          })}
        </Text>
      </Modal>
    </>
  );
};

export default SessionAssignationView;
